import React, { useState, useEffect, useContext } from "react";
import styled, { keyframes } from "styled-components";
import logo from "../Assets/logo.png";
import { AuthContext } from "../hooks/useLogin";
import { useNavigate } from "react-router-dom";

const LoginModal = ({ isOpen, toggleModal, onSwitchToSignUp, onLoginSuccess }) => {
  const { login, error } = useContext(AuthContext);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [errorMessage, setErrorMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen) {
      setCredentials({ email: "", password: "" });
      setShowError(false);
      setErrorMessage(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (error) {
      setErrorMessage(error);
      setShowError(true);
    }
  }, [error]);

  const handleChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMessage(null);
    setShowError(false);
    setLoading(true);

    const result = await login(credentials.email, credentials.password);

    setLoading(false);

    if (result.success) {
      localStorage.setItem("userToken", result.user.token);
      localStorage.setItem("userData", JSON.stringify(result.user));
      localStorage.setItem("userRole", result.user.role);
      localStorage.setItem("userId", result.user.id.toString());

      window.dispatchEvent(new Event("userLoggedIn"));

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        toggleModal();
        if (typeof onLoginSuccess === "function") {
          onLoginSuccess();
        }

        const currentPath = window.location.pathname;
        if (result.user.role === "Administrator" && currentPath !== "/AdminHomepage") {
          navigate("/AdminHomepage");
        } else if (currentPath !== "/") {
          navigate("/");
        }
      }, 2000);
    } else {
      setErrorMessage(result.message);
      setShowError(true);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <ModalOverlay>
        <ModalContainer>
          <CloseButton onClick={toggleModal}>×</CloseButton>
          <Logo src={logo} alt="INA-ANI Logo" />
          <Title>SIGN IN</Title>
          <Subtitle>Welcome to Ina-Ani!</Subtitle>
          <Form onSubmit={handleLogin}>
            <InputContainer>
              <Input
                type="email"
                name="email"
                placeholder=" "
                value={credentials.email}
                onChange={handleChange}
                required
              />
              <Label>Email or Phone Number*</Label>
            </InputContainer>

            <InputContainer>
              <Input
                type="password"
                name="password"
                placeholder=" "
                value={credentials.password}
                onChange={handleChange}
                required
              />
              <Label>Password*</Label>
            </InputContainer>

            <LoginButton type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Spinner />
                </>
              ) : (
                "SIGN IN"
              )}
            </LoginButton>
          </Form>

          <SignUpPrompt>
            Don’t have an account?{" "}
            <SignUpLink
              onClick={() => {
                if (typeof onSwitchToSignUp === "function") {
                  toggleModal();
                  onSwitchToSignUp();
                } else {
                  console.error("onSwitchToSignUp is not a function");
                }
              }}
            >
              Sign up
            </SignUpLink>
          </SignUpPrompt>
        </ModalContainer>
        {showSuccess && (
          <MessageOverlay>
            <SuccessMessage>
              <SuccessIconWrapper>
                <SuccessIcon>✓</SuccessIcon>
              </SuccessIconWrapper>
              <MessageTitle>Success</MessageTitle>
              <MessageDescription>
                Login successful! Redirecting you now...
              </MessageDescription>
              <ContinueButton onClick={() => setShowSuccess(false)}>Continue</ContinueButton>
            </SuccessMessage>
          </MessageOverlay>
        )}
        {showError && (
          <MessageOverlay>
            <ErrorMessage>
              <ErrorIconWrapper>
                <ErrorIcon>✗</ErrorIcon>
              </ErrorIconWrapper>
              <MessageTitle>Error</MessageTitle>
              <MessageDescription>{errorMessage || "An error occurred. Please try again."}</MessageDescription>
              <TryAgainButton onClick={() => setShowError(false)}>Try again</TryAgainButton>
            </ErrorMessage>
          </MessageOverlay>
        )}
      </ModalOverlay>
    </>
  );
};

export default LoginModal;

// ---------------- Styled Components ----------------

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: #ffffff;
  border-radius: 12px;
  width: 350px;
  padding: 2rem;
  position: relative;
  text-align: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
`;

const MessageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
`;

const SuccessMessage = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 2rem;
  width: 90%;
  max-width: 350px;
  text-align: center;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  animation: ${fadeIn} 0.3s ease-in;
`;

const ErrorMessage = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 2rem;
  width: 90%;
  max-width: 350px;
  text-align: center;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  animation: ${fadeIn} 0.3s ease-in;
`;

const SuccessIconWrapper = styled.div`
  width: 60px;
  height: 60px;
  background: #007bff;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto 1rem;
`;

const ErrorIconWrapper = styled.div`
  width: 60px;
  height: 60px;
  background: #ff3333;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto 1rem;
`;

const SuccessIcon = styled.span`
  font-size: 2rem;
  color: #ffffff;
`;

const ErrorIcon = styled.span`
  font-size: 2rem;
  color: #ffffff;
`;

const MessageTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: bold;
  color: #333333;
  margin-bottom: 0.5rem;
`;

const MessageDescription = styled.p`
  font-size: 0.9rem;
  color: #666666;
  margin-bottom: 1.5rem;
`;

const ContinueButton = styled.button`
  background: #007bff;
  color: white;
  padding: 0.8rem 2rem;
  border: none;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;

  &:hover {
    background: #0056b3;
  }
`;

const TryAgainButton = styled.button`
  background: #ff3333;
  color: white;
  padding: 0.8rem 2rem;
  border: none;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;

  &:hover {
    background: #cc0000;
  }
`;

const Logo = styled.img`
  width: 80px;
  height: auto;
  margin: 0 auto 1rem;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: bold;
  color: #004d00;
  margin-top: -5px;
`;

const Subtitle = styled.p`
  font-size: 2rem;
  color: #004d00;
  margin: 0.5rem 0 1rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Label = styled.label`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1rem;
  color: #777;
  background: white;
  padding: 0 5px;
  transition: all 0.3s ease-in-out;
  pointer-events: none;

  input:focus + &,
  input:not(:placeholder-shown) + & {
    top: 5px;
    font-size: 0.5rem;
    color: #004d00;
  }
`;

const InputContainer = styled.div`
  position: relative;
  width: 95%;
  margin: 0.5rem auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #004d00;
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem;
  border: 1.5px solid #004d00;
  border-radius: 5px;
  font-size: 1rem;
  outline: none;
  background: transparent;

  &:focus ~ label,
  &:not(:placeholder-shown) ~ label {
    top: 5px;
    font-size: 0.85rem;
    color: #004d00;
  }
`;

const LoginButton = styled.button`
  width: 100%;
  padding: 0.8rem;
  background: #004d00;
  color: #ffffff;
  border: none;
  border-radius: 25px;
  font-size: 1rem;
  cursor: pointer;
  margin-top: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background: #003300;
  }

  &:disabled {
    background: #004d00;
    cursor: not-allowed;
  }
`;

const Spinner = styled.span`
  border: 2px solid #ffffff;
  border-top: 2px solid #004d00;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SignUpPrompt = styled.div`
  margin-top: 1rem;
  font-size: 0.9rem;
`;

const SignUpLink = styled.span`
  color: #004d00;
  font-weight: bold;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  color: #999999;
  font-size: 1.5rem;
  cursor: pointer;

  &:hover {
    color: #004d00;
  }
`;